import { useMemo, type CSSProperties } from "react";
import {
  SVGContainer,
  useDefaultColorTheme,
  useEditor,
  useValue,
} from "tldraw";
import type { TldrawAgent } from "../../agent/TldrawAgent";
import { useAgents } from "../../agent/TldrawAgentAppProvider";
import type { ActiveGesture } from "../../agent/managers/AgentGestureManager";
import {
  GESTURE_DRAW_MS,
  GESTURE_FADE_MS,
  GESTURE_HOLD_MS,
} from "../../agent/managers/AgentGestureManager";
import {
  DEFAULT_GESTURE_COLOR,
  gestureGeometry,
  STROKE_SCREEN_PX,
} from "./gestureGeometry";

/**
 * Renders transient tutor gestures for all agents. Mounted in tldraw's
 * `Overlays` slot beside `AllAgentCursors`, never as a shape — see
 * `GestureActionUtil` for why that is load-bearing.
 */
export function AllAgentGestures() {
  const agents = useAgents();

  return (
    <>
      {agents.map((agent) => (
        <AgentGestures key={agent.id} agent={agent} />
      ))}
    </>
  );
}

/** Renders the gestures currently active for a single agent. */
export function AgentGestures({ agent }: { agent: TldrawAgent }) {
  const gestures = useValue("gestures", () => agent.gestures.getGestures(), [
    agent,
  ]);

  return (
    <>
      {gestures.map((gesture) => (
        <GestureMark key={gesture.id} gesture={gesture} />
      ))}
    </>
  );
}

/**
 * One gesture: a hand-drawn circle or underline that reveals itself along its
 * own length, holds, then fades.
 *
 * The reveal is a `stroke-dasharray` trick, not anything measured in JS.
 * `pathLength={1}` renormalises the path so one unit IS the whole stroke, which
 * makes the CSS keyframes identical for a tight circle round a digit and a long
 * underline under a sentence — no `getTotalLength()`, no layout read, nothing
 * needing a real browser. Both passes live in one `d`, so the sweep draws them
 * in sequence. `prefers-reduced-motion` (canvas.css) drops the reveal and keeps
 * the fade — the fade is what reads as "pointing".
 */
function GestureMark({ gesture }: { gesture: ActiveGesture }) {
  const editor = useEditor();
  const theme = useDefaultColorTheme();

  // Recomputed on zoom only through the stroke width — the geometry itself is
  // page-space and camera-independent, and the seed keeps the jitter stable so
  // the mark does not reshuffle itself mid-pinch.
  const geometry = useMemo(
    () => gestureGeometry(gesture.kind, gesture.bounds, gesture.seed),
    [gesture.kind, gesture.bounds, gesture.seed],
  );

  const strokeWidth = useValue(
    "gestureStrokeWidth",
    () => STROKE_SCREEN_PX / editor.getZoomLevel(),
    [editor],
  );

  // The wire carries a bare string, so it may name no palette entry at all (or
  // a non-colour key like `id`). Both fall through to the default rather than
  // to an invisible stroke.
  const solidOf = (key: string | undefined): string | undefined => {
    const entry = key ? theme[key as keyof typeof theme] : undefined;
    return typeof entry === "object" && entry !== null
      ? entry.solid
      : undefined;
  };
  const stroke = solidOf(gesture.color) ?? solidOf(DEFAULT_GESTURE_COLOR)!;

  return (
    <SVGContainer
      className="agent-gesture"
      style={
        {
          top: geometry.top,
          left: geometry.left,
          width: geometry.width,
          height: geometry.height,
          overflow: "visible",
          pointerEvents: "none",
          // The three the stylesheet reads. The hold is not one of them: it is
          // the gap between the two animations, i.e. the fade's delay.
          "--gesture-draw-ms": `${GESTURE_DRAW_MS}ms`,
          "--gesture-fade-ms": `${GESTURE_FADE_MS}ms`,
          "--gesture-fade-delay-ms": `${GESTURE_DRAW_MS + GESTURE_HOLD_MS}ms`,
        } as CSSProperties
      }
    >
      <path
        className="agent-gesture-stroke"
        d={geometry.d}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
      />
    </SVGContainer>
  );
}
